import { ArrowRight, BarChart3, Bot, KeyRound, MousePointerClick, Search, ShieldCheck } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import { ClosingBand, Crumbs, PublicHeader, breadcrumbs, jsonLd } from "@/components/UseCasePage";
import { useHeadMeta } from "@/lib/head";
import { SITE_URL } from "@/content/site";
import { INTEGRATIONS, INTEGRATIONS_PAGE, INTEGRATION_GROUPS } from "@/content/integrations.mjs";
import "./HomepageFonts.css";
import "./Homepage.css";
import "./UseCases.css";

const ICONS = { "search-console": Search, ga4: BarChart3, "google-sign-in": KeyRound, anthropic: Bot, openai: Bot, openrouter: Bot, bing: Search, clarity: MousePointerClick };

export default function IntegrationsPage() {
  useHeadMeta({ title: INTEGRATIONS_PAGE.title, robots: "index, follow" });
  const path = INTEGRATIONS_PAGE.path;
  const crumbs = [{ name: "Home", path: "/" }, { name: "Integrations", path }];
  const graph = { "@context": "https://schema.org", "@graph": [
    { "@type": "WebPage", "@id": `${SITE_URL}${path}#webpage`, url: `${SITE_URL}${path}`, name: INTEGRATIONS_PAGE.title, description: INTEGRATIONS_PAGE.description, inLanguage: "en-GB", isPartOf: { "@id": `${SITE_URL}/#website` }, about: { "@id": `${SITE_URL}/#software` }, publisher: { "@id": `${SITE_URL}/#organization` } },
    breadcrumbs(crumbs),
  ] };
  return <div className="ss-home ss-uc">
    <script type="application/ld+json" dangerouslySetInnerHTML={jsonLd(graph)} />
    <a className="ss-skip" href="#main">Skip to content</a>
    <PublicHeader />
    <main id="main">
      <section className="ss-uc-hero"><div className="ss-container">
        <Crumbs items={crumbs} />
        <p className="ss-eyebrow">{INTEGRATIONS_PAGE.eyebrow}</p>
        <h1>{INTEGRATIONS_PAGE.h1}</h1>
        <p className="ss-lead">{INTEGRATIONS_PAGE.lead}</p>
        <ul className="ss-uc-ticks"><li><ShieldCheck size={16} aria-hidden="true" />Read-only Google access</li><li><ShieldCheck size={16} aria-hidden="true" />Remove access any time in your Google account</li></ul>
      </div></section>
      {INTEGRATION_GROUPS.map(group => <section key={group} className="ss-container ss-section ss-int-group" aria-labelledby={`int-${group}`}>
        <h2 id={`int-${group}`}>{group}</h2>
        <ul className="ss-int-grid">{INTEGRATIONS.filter(i => i.group === group).map(i => { const Icon = ICONS[i.id]; return <li key={i.id} className="ss-int-card" data-testid={`integration-${i.id}`}>
          <div className="ss-int-head"><span className="ss-uc-icon"><Icon size={22} aria-hidden="true" /></span><span className={`ss-int-status ss-int-${i.status.toLowerCase()}`}>{i.status}</span></div>
          <h3>{i.name}</h3>
          <p>{i.summary}</p>
          {i.reads.length > 0 && <><p className="ss-int-label">What it uses</p><ul className="ss-int-reads">{i.reads.map(r => <li key={r}>{r}</li>)}</ul></>}
          <p className="ss-int-label">Access</p><p className="ss-int-access">{i.access}</p>
        </li>; })}</ul>
      </section>)}
      <section className="ss-container ss-section ss-uc-limits" aria-labelledby="int-privacy">
        <h2 id="int-privacy">How your data is handled</h2>
        <p>SEO Signals reads only the properties you choose and cannot change anything in your Google account. Your question and selected site's report summary are shared with Anthropic or OpenAI only after you explicitly allow that recipient for that site. You can decline and keep using data reports, or withdraw permission in Settings. OpenRouter sharing of connected data is currently unavailable. Read the full details, including Google's Limited Use requirements, in the <a href="/privacy">privacy policy</a>.</p>
        <p><a className="ss-button ss-secondary" href="/use-cases">See what SEO Signals finds <ArrowRight size={16} aria-hidden="true" /></a></p>
      </section>
      <ClosingBand />
    </main>
    <SiteFooter />
  </div>;
}
